/* ----------------- React --------------- */
import { useState } from "react";

import DateTimePicker from "components/DateTimePicker";
/* ----------------- Child components --------------- */
import AccountView from "../components/AccountView";
import DeleteConfirmationDialog from "../components/dialogs/DeleteConfirmationDialog";
import DeleteFeedbackDialog from "../components/dialogs/DeleteFeedbackDialog";
import { SignoutDialog } from "../components/dialogs/SignOutDialog";

import { useThemeContext } from "@contexts/ThemeContext";
import { useExamContext } from "@features/exam";
import { useDevice } from "@hooks/useDevice";
/* ----------------- Hooks --------------- */
import useAccountSettings from "../hooks/useAccountSettings";
import { useDelete } from "../hooks/useDelete";

import { PurchaseDialog } from "@features/dashboard/components/PurchaseDialog";
import { FaqsDialog } from "@features/faqs";
import { isWeb } from "@helpers/platform";
import { DATE_FORMAT } from "@memorang/constants/src/date";
/* ----------------- UI Libraries --------------- */
import { Box, ScrollContainer, useToast } from "@memorang/ui";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Card } from "react-native-paper";

const AccountSettings = () => {
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [showSignOutDialog, setShowSignOutDialog] = useState(false);
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
	const [showFaqsDialog, setShowFaqsDialog] = useState(false);
	const { t } = useTranslation();
	const {
		toggleShowDeleteConfirmationDialog,
		toggleShowDeleteFeedbackDialog,
		showDeleteConfirmationDialog,
		showDeleteFeedbackDialog,
		selectedReasons,
		feedback,
		handleSetFeedback,
		handleSendFeedback,
		checkboxStates,
		handleChangeCheckboxState,
	} = useDelete();

	const { toggleDarkMode, isDarkMode } = useThemeContext();
	const { showToast } = useToast();
	const toggleShowSignOutDialog = () => {
		setShowSignOutDialog((prev) => !prev);
	};
	const { currentExamContext, updateExamDate } = useExamContext();
	const toggleShowDatePicker = () => {
		setShowDatePicker((prev) => !prev);
	};
	const toggleShowPurchaseDialog = () => {
		setShowPurchaseDialog((prev) => !prev);
	};

	const toggleShowFaqsDialog = () => {
		setShowFaqsDialog((prev) => !prev);
	};

	const handleToggleDarkMode = () => {
		toggleDarkMode();
		showToast({
			message: `${isDarkMode ? "Light" : "Dark"} mode has been enabled`,
			duration: 1000,
		});
	};

	const { getAccountSections } = useAccountSettings({
		toggleShowDatePicker,
		toggleShowSignOutDialog,
		toggleDarkMode: handleToggleDarkMode,
		toggleShowDeleteFeedbackDialog,
		toggleShowPurchaseDialog,
		toggleShowFaqsDialog,
	});

	const sections = getAccountSections();
	const { isMobile } = useDevice();

	const Wrapper = isMobile ? Box : Card;
	const verticalPadding = isMobile ? undefined : 16;

	return (
		<ScrollContainer maxWidth="sm">
			<Wrapper
				style={{
					marginTop: 16,
					marginBottom: 16,
					paddingTop: verticalPadding,
					paddingBottom: verticalPadding,
				}}
			>
				<AccountView sections={sections} />
			</Wrapper>
			{showDatePicker ? (
				<DateTimePicker
					isVisible
					onCancel={toggleShowDatePicker}
					initialDate={currentExamContext?.examDate}
					onConfirm={(date) => {
						if (!isWeb) {
							toggleShowDatePicker();
						}
						updateExamDate(date);
						const formattedDate =
							DateTime.fromJSDate(date).toFormat(DATE_FORMAT);
						showToast({
							message: t("account.examDateUpdated", { date: formattedDate }),
							duration: 3000,
						});
					}}
				/>
			) : null}
			{showSignOutDialog && (
				<SignoutDialog
					visible={showSignOutDialog}
					handleClose={toggleShowSignOutDialog}
				/>
			)}
			{showDeleteFeedbackDialog && (
				<DeleteFeedbackDialog
					visible={showDeleteFeedbackDialog}
					handleDismiss={toggleShowDeleteFeedbackDialog}
					handleShowDeleteConfirmation={toggleShowDeleteConfirmationDialog}
					feedback={feedback}
					handleSetFeedback={handleSetFeedback}
					handleSelectOption={handleChangeCheckboxState}
					checkboxStates={checkboxStates}
					selectedReasons={selectedReasons}
				/>
			)}
			{showDeleteConfirmationDialog && (
				<DeleteConfirmationDialog
					visible={showDeleteConfirmationDialog}
					handleDismiss={toggleShowDeleteConfirmationDialog}
					handleSendFeedback={handleSendFeedback}
				/>
			)}
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={toggleShowPurchaseDialog}
					productType="STUDY_PACK"
				/>
			)}
			{showFaqsDialog && (
				<FaqsDialog
					visible={showFaqsDialog}
					handleClose={toggleShowFaqsDialog}
				/>
			)}
		</ScrollContainer>
	);
};

export default AccountSettings;
